import { ActionDefinition, ActionTypes, ActionableDefinitions, Table, TableRow } from '@lifebit-ai/lds'
import React from 'react'

import { isImportRequestAwaitingManagerDecision } from 'services/import-requests/importRequestUtils'
import type { ImportRequestVerificationListItem } from 'services/import-requests/types'

export enum RowOverlayActionTypes {
  AssignTo = 'assignTo',
  Unassign = 'unassign'
}

/**
 * Derives the hover-overlay actions for an import request row. Assign/Unassign are only
 * actionable while a request is pending verification. "Assign to" opens the modal preloaded
 * with the current assignee so an assigned request can be reassigned (LP-114398), while
 * "Unassign" stays available as a one-click shortcut for assigned rows.
 */
export const getImportRequestRowActions = (row: TableRow<ImportRequestVerificationListItem>) => {
  const dropdownActions: ActionDefinition[] = []

  if (isImportRequestAwaitingManagerDecision(row.original)) {
    dropdownActions.push({ type: ActionTypes.Button, name: 'Assign to', id: RowOverlayActionTypes.AssignTo })

    if (row.original.assignedTo) {
      dropdownActions.push({ type: ActionTypes.Button, name: 'Unassign', id: RowOverlayActionTypes.Unassign })
    }
  }

  return {
    visibleActions: [],
    dropdownActions
  }
}

interface ImportRequestsRowHoverOverlayProps {
  row: TableRow<ImportRequestVerificationListItem>
  onAssignTo: (requestId: string, assignedTo?: string) => void
  onUnassignFrom: (requestId: string) => void
}

const ImportRequestsRowHoverOverlay: React.FC<ImportRequestsRowHoverOverlayProps> = ({
  row,
  onAssignTo,
  onUnassignFrom
}) => {
  const handleOnActionClicked = (
    clickedRow: TableRow<ImportRequestVerificationListItem>,
    action: ActionableDefinitions
  ) => {
    switch (action.id) {
      case RowOverlayActionTypes.AssignTo:
        onAssignTo(clickedRow.original._id, clickedRow.original.assignedTo?._id)
        break
      case RowOverlayActionTypes.Unassign:
        onUnassignFrom(clickedRow.original._id)
        break
      default:
        break
    }
  }

  return (
    <Table.RowHoverOverlay
      row={row}
      isSelected={false}
      onActionClicked={handleOnActionClicked}
      actions={getImportRequestRowActions(row)}
    />
  )
}

export default ImportRequestsRowHoverOverlay
